import {useMemo} from "react";
import MetricDashCard from "../../../../../components/dash-card/metric-dash-card.jsx";
import SentryEchart from "../../../../../components/echarts/sentry-echart.jsx";
import useGraphViewportWidth from "../../../../../hooks/use-graph-viewport-width.js";
import {DASH_BREAKPOINTS, fluidPx} from "../../../../../utils/responsive-graph.js";

export default function RiskLevelCharts({
    total,
    activeTotal,
    appsActive,
    riskLevels,
    className = "",
    chartTitle = "Grau de risco",
}) {
    const vw = useGraphViewportWidth();
    const totalRegistrations = Number(total) || 0;
    const activeRegistrations = Number(activeTotal) || 0;
    const inactiveRegistrations = Math.max(totalRegistrations - activeRegistrations, 0);

    const donutOption = useMemo(() => {
        const isCompact = vw < DASH_BREAKPOINTS.desktop;
        const labelFs = fluidPx(11, 24, vw);
        const totalFs = fluidPx(28, 34, vw);
        const pieRadius = isCompact ? ["65%", "89%"] : ["68%", "95%"];

        return {
            tooltip: {
                show: true,
                formatter: params => `${params.name}: ${params.value} (${params.percent.toFixed(0)}%)`,
            },
            title: {
                text: `{value|${totalRegistrations}}\n{label|Cadastros}`,
                left: "50%",
                top: "50%",
                textAlign: "center",
                textVerticalAlign: "middle",
                padding: 0,
                textStyle: {
                    rich: {
                        value: {
                            color: "#E8E8E8",
                            fontSize: totalFs,
                            fontWeight: 600,
                            lineHeight: totalFs + 5,
                        },
                        label: {
                            color: "#AAAAAA",
                            fontSize: labelFs,
                            fontWeight: 300,
                            lineHeight: labelFs + 5,
                        },
                    },
                },
            },
            series: [
                {
                    type: "pie",
                    radius: pieRadius,
                    center: ["50%", "50%"],
                    silent: true,
                    animation: false,
                    tooltip: {show: false},
                    label: {show: false},
                    labelLine: {show: false},
                    z: 1,
                    data: [
                        {
                            value: 1,
                            itemStyle: {color: "rgba(255, 255, 255, 0.08)"},
                        },
                    ],
                },
                {
                    type: "pie",
                    radius: pieRadius,
                    center: ["50%", "50%"],
                    avoidLabelOverlap: true,
                    z: 2,
                    emphasis: {
                        scale: true,
                        scaleSize: 4,
                    },
                    itemStyle: {borderRadius: 20},
                    label: {show: false},
                    labelLine: {show: false},
                    data: [
                        {
                            value: inactiveRegistrations,
                            name: "Inativos",
                            itemStyle: {color: "rgba(255, 255, 255, 0)"},
                            emphasis: {disabled: true},
                        },
                        {
                            value: activeRegistrations,
                            name: "Ativos",
                            itemStyle: {
                                color: {
                                    type: "linear",
                                    x: 0,
                                    y: 1,
                                    x2: 0.85,
                                    y2: 0,
                                    colorStops: [
                                        {offset: 0, color: "#00E7DF"},
                                        {offset: 0.28, color: "#20CFEA"},
                                        {offset: 0.52, color: "#168FE3"},
                                        {offset: 0.78, color: "#006BC4"},
                                        {offset: 1, color: "#004A92"},
                                    ],
                                },
                            },
                        },
                    ],
                },
            ],
        };
    }, [activeRegistrations, inactiveRegistrations, totalRegistrations, vw]);

    const riskLevelsOption = useMemo(() => {
        const items = Array.isArray(riskLevels)
            ? riskLevels.map(item => ({
                label: item.label ?? "",
                value: Number(item.qty) || 0,
            }))
            : [];
        const labelFs = fluidPx(12, 21, vw);
        const valueFs = fluidPx(14, 20, vw);
        const categoryGap = labelFs + 10;
        const max = Math.max(...items.map(item => item.value), 1);
        const labels = items.map(item => item.label);
        const values = items.map(item => item.value);

        return {
            grid: {
                top: labelFs,
                left: "5%",
                right: "12%",
                bottom: 0,
                outerBounds: {
                    top: labelFs,
                    left: "5%",
                    right: "12%",
                    bottom: 0,
                },
                outerBoundsContain: "axisLabel",
            },
            tooltip: {
                show: true,
                appendToBody: true,
                confine: false,
                extraCssText: "max-width: 320px; white-space: normal;",
                formatter: params => `${params.name}: ${params.value}`,
            },
            xAxis: {
                type: "value",
                max,
                show: false,
            },
            yAxis: {
                type: "category",
                data: labels,
                inverse: true,
                axisLabel: {show: false},
                axisLine: {show: false},
                axisTick: {show: false},
            },
            series: [
                {
                    type: "bar",
                    barWidth: 13,
                    itemStyle: {
                        color: {
                            type: "linear",
                            x: 0,
                            y: 0,
                            x2: 1,
                            y2: 0,
                            colorStops: [
                                {offset: 0, color: "#003878"},
                                {offset: 0.28, color: "#0080ED"},
                                {offset: 0.52, color: "#1CEAE6"},
                                {offset: 0.72, color: "#3E98D9"},
                                {offset: 1, color: "#003878"},
                            ],
                        },
                        borderRadius: 20,
                    },
                    label: {
                        show: true,
                        position: [0, -4],
                        align: "left",
                        verticalAlign: "bottom",
                        color: "#AAAAAA",
                        fontSize: labelFs,
                        fontWeight: 400,
                        formatter: params => labels[params.dataIndex],
                    },
                    showBackground: true,
                    backgroundStyle: {
                        color: "#3C3C4A",
                        borderRadius: 12,
                    },
                    data: values,
                },
                {
                    type: "bar",
                    barWidth: 13,
                    barCategoryGap: categoryGap,
                    barGap: "-100%",
                    silent: true,
                    tooltip: {show: false},
                    itemStyle: {color: "transparent"},
                    label: {
                        show: true,
                        position: "right",
                        distance: 5,
                        color: "#FFFFFF",
                        fontSize: valueFs,
                        fontWeight: 600,
                        formatter: params => values[params.dataIndex],
                    },
                    data: values.map(() => max),
                },
            ],
        };
    }, [riskLevels, vw]);

    return (
        <article className={`box-border -mt-1 h-[calc(100%+12px)] min-h-0 w-full min-w-0 grid grid-rows-[minmax(0,0.9fr)_minmax(0,1.1fr)] ${className}`}>
            <section className="h-full min-h-0 min-w-0 grid grid-cols-[48%_52%] gap-(--dash-pad-sm)">
                <div className="flex h-full min-h-0 min-w-0 items-center justify-center">
                    <SentryEchart
                        className="h-full min-h-0 w-full"
                        option={donutOption}
                    />
                </div>
                <div className="h-full min-h-0 min-w-0 grid grid-rows-[auto_1fr] justify-center space-y-3">
                    <div className="flex flex-col items-start justify-start gap-2 pt-[10%] text-xs 2xl:text-sm">
                        <div className="flex items-center gap-2">
                            <span
                                aria-hidden="true"
                                className="size-4 shrink-0 rounded-full bg-[linear-gradient(45deg,#00E7DF_0%,#20CFEA_28%,#168FE3_52%,#006BC4_78%,#004A92_100%)] 2xl:size-4.5"
                            />
                            <span className="font-semibold text-[#FFFFFF] text-[16px]">{activeRegistrations}</span>
                            <span className="font-normal text-[#AAAAAA] text-[16px]">Ativos</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span
                                aria-hidden="true"
                                className="size-4 shrink-0 rounded-full bg-[rgba(255,255,255,0.08)] 2xl:size-4.5"
                            />
                            <span className="font-semibold text-[#FFFFFF] text-[16px]">{inactiveRegistrations}</span>
                            <span className="font-normal text-[#AAAAAA] text-[16px]">Inativos</span>
                        </div>
                    </div>
                    <div className="flex min-h-0 min-w-0 items-start justify-start">
                        <MetricDashCard
                            title="Apps ativos"
                            counterCard
                            value={appsActive ?? 0}
                            width={117}
                            height={80}
                        />
                    </div>
                </div>
            </section>
            <section className="flex h-full min-h-0 min-w-0 overflow-hidden">
                <SentryEchart
                    className="h-full min-h-0 w-full"
                    option={riskLevelsOption}
                    notMerge
                    title={chartTitle}
                    titleClass="mb-0! translate-y-2"
                />
            </section>
        </article>
    )
}
